import React, { useEffect, useState } from 'react';
import { MdAdd, MdAttachMoney, MdSave } from 'react-icons/md';
import { AlertType, PriceList } from '../../@types';
import {
  AlertModal,
  Card,
  PageHeader,
  SectionHeader,
} from '../../components/atoms';
import LoadingSpinner from '../../components/atoms/LoadingSpinner';
import AdminService from '../../services/AdminService';

interface AddPriceListPayload {
  category: string;
  type: string;
  price: number;
  percentageDiscount: number;
}

const SeasonPriceList: React.FC = () => {
  const [priceList, setPriceList] = useState<PriceList[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddMode, setIsAddMode] = useState(false);
  const [isLoadingAdd, setIsLoadingAdd] = useState(false);
  const [newPriceItem, setNewPriceItem] = useState({
    category: '',
    type: '',
    price: '',
    percentageDiscount: 0,
  });
  const [formErrors, setFormErrors] = useState<{
    category?: string;
    type?: string;
    price?: string;
    percentageDiscount?: string;
  }>({});
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: AlertType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const showAlert = (type: AlertType, title: string, message: string) => {
    setAlertModal({
      isOpen: true,
      type,
      title,
      message,
    });
  };

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    const fetchPriceList = async () => {
      try {
        setLoading(true);
        const responseData =
          (await AdminService.getCurrentPriceList()) as unknown as PriceList[];

        setPriceList(responseData);
      } catch (err) {
        console.error('Error fetching price list:', err);
        showAlert(
          'error',
          'Error',
          'Failed to load price list. Please try again.',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPriceList();
  }, []);

  const validateForm = (): boolean => {
    const errors: {
      category?: string;
      type?: string;
      price?: string;
      percentageDiscount?: string;
    } = {};

    if (!newPriceItem.category.trim()) {
      errors.category = 'Category is required';
    }

    if (!newPriceItem.type.trim()) {
      errors.type = 'Type is required';
    }

    if (!newPriceItem.price.trim()) {
      errors.price = 'Price is required';
    } else {
      const priceValue = parseFloat(newPriceItem.price);
      if (isNaN(priceValue) || priceValue < 0) {
        errors.price = 'Price must be a non-negative number';
      } else if (
        priceValue === 0 &&
        (!newPriceItem.percentageDiscount ||
          newPriceItem.percentageDiscount === 0)
      ) {
        errors.price =
          'Price must be greater than 0 when no discount is provided';
      }
    }

    if (
      newPriceItem.percentageDiscount !== undefined &&
      newPriceItem.percentageDiscount !== 0
    ) {
      const discountValue =
        typeof newPriceItem.percentageDiscount === 'string'
          ? parseFloat(newPriceItem.percentageDiscount)
          : newPriceItem.percentageDiscount;
      if (isNaN(discountValue) || discountValue < 0 || discountValue > 100) {
        errors.percentageDiscount =
          'Percentage discount must be between 0 and 100';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (
    field: 'category' | 'type' | 'price' | 'percentageDiscount',
    value: string,
  ) => {
    setNewPriceItem(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleAddPriceItem = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoadingAdd(true);

    try {
      const payload: AddPriceListPayload = {
        category: newPriceItem.category.trim(),
        type: newPriceItem.type.trim(),
        price: parseFloat(newPriceItem.price),
        percentageDiscount:
          parseFloat(newPriceItem.percentageDiscount.toString()) || 0,
      };

      await AdminService.addPriceList(payload as any);

      // Reset form
      setNewPriceItem({
        category: '',
        type: '',
        price: '',
        percentageDiscount: 0,
      });
      setFormErrors({});
      setIsAddMode(false);

      // Refresh the price list
      const responseData =
        (await AdminService.getCurrentPriceList()) as unknown as PriceList[];
      setPriceList(responseData);

      showAlert('success', 'Success', 'Price item added successfully!');
    } catch (err) {
      console.error('Error adding price item:', err);
      showAlert(
        'error',
        'Error',
        'Failed to add price item. Please try again.',
      );
    } finally {
      setIsLoadingAdd(false);
    }
  };

  const handleCancelAdd = () => {
    setNewPriceItem({
      category: '',
      type: '',
      price: '',
      percentageDiscount: 0,
    });
    setFormErrors({});
    setIsAddMode(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={MdAttachMoney}
        title="Season Price List"
        description="View and manage pricing for all ticket categories and types."
      />

      {/* Add New Price Item Section */}
      <Card className="flex flex-col">
        <SectionHeader
          title="Add New Price Item"
          icon={MdAdd}
          showEditButton={true}
          isEditMode={isAddMode}
          onEditToggle={() => setIsAddMode(!isAddMode)}
        />

        {isAddMode && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label
                  htmlFor="category"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Category
                </label>
                <select
                  id="category"
                  value={newPriceItem.category}
                  onChange={e => handleInputChange('category', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    formErrors.category ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Select a category</option>
                  <option value="Skating">Skating</option>
                  <option value="Season Pass">Season Pass</option>
                  <option value="Parties">Parties</option>
                  <option value="Lessons">Lessons</option>
                  <option value="Hockey">Hockey</option>
                </select>
                {formErrors.category && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.category}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="type"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Type
                </label>
                <input
                  type="text"
                  id="type"
                  value={newPriceItem.type}
                  onChange={e => handleInputChange('type', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    formErrors.type ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Adult, Child"
                  required
                />
                {formErrors.type && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.type}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="price"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Price ($)
                </label>
                <input
                  type="number"
                  id="price"
                  value={newPriceItem.price}
                  onChange={e => handleInputChange('price', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    formErrors.price ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  required
                />
                {formErrors.price && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.price}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="percentageDiscount"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Percentage Discount
                </label>
                <input
                  type="number"
                  id="percentageDiscount"
                  value={newPriceItem.percentageDiscount}
                  onChange={e =>
                    handleInputChange('percentageDiscount', e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    formErrors.percentageDiscount
                      ? 'border-red-300'
                      : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  max="100"
                />
                {formErrors.percentageDiscount && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.percentageDiscount}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelAdd}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddPriceItem}
                disabled={isLoadingAdd}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                <MdSave className="mr-2" />
                {isLoadingAdd ? 'Adding...' : 'Add Price Item'}
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Main Content */}
      <Card>
        {priceList.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">No price list items found.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discount %
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Custom
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {priceList.map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${item.price.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.percentageDiscount > 0
                      ? `${item.percentageDiscount}%`
                      : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        item.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {item.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        item.isCustom
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {item.isCustom ? 'Custom' : 'Standard'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};

export default SeasonPriceList;
